import React, { useEffect, useState, useMemo } from "react";
import "../styles/home.css";
import "../styles/table.css";
import "../styles/theme.css";
import Layout from "./Layout";
import {
  getDispensedAndLevelData,
  parseTankData,
  prepareChartData,
  runDispVSLevels,
  prepareTemperatureChartData,
  prepareTankLevelChartData,
} from "../functions/api/transactions";
import DropdownComponent from "./display/DropDownComponent";
import { getSiteNames } from "../functions/api/details";
import { DispDataEntry } from "../interfaces/interfaces";
import ReusableLineChart from "./display/LineChartComponent";
import SmallTableComponent from "./display/SmallTable";
import DispLevelAndTempChart from "./display/DispLevelAndTempChart";
import DescriptionList from "./display/DetailsComponent";

const getYesterday = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

const DispVSLevelComponent: React.FC = () => {
  const [dispAndLevelData, setDispAndLevelData] = useState<any[]>([]);
  const [dispVSLevelData, setDispVSLevelData] = useState<any[]>([]);
  const [selectedFromDate, setSelectedFromDate] = useState<string>(getYesterday());
  const [code, setSelectedCode] = useState<string>("Lusaka");
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const populateSiteItems = async () => {
    const data = await getSiteNames();
    setSiteOptions(Array.isArray(data) ? data : []);
  };

  const loadAllData = async (siteCode: string, fromDate: string) => {
    setLoading(true);
    const dispData = await getDispensedAndLevelData(siteCode, fromDate);
    const validData = Array.isArray(dispData) ? dispData : [];
    setDispAndLevelData(validData);

    if (validData.length > 0) {
      const raw = await runDispVSLevels(validData);
      const parsed = parseTankData(raw);
      setDispVSLevelData(parsed);
    } else {
      setDispVSLevelData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    populateSiteItems();
    loadAllData(code, selectedFromDate);
  }, []);

  const handleFromDateChange = (newDate: string) => {
    setSelectedFromDate(newDate);
    loadAllData(code, newDate);
  };

  const handleDropdownChange = (newCode: string) => {
    setSelectedCode(newCode);
    loadAllData(newCode, selectedFromDate);
  };

  // interpolation helper
  function interpolateMissing(data: any[], yKeys: string[]): any[] {
    const cloned = [...data];
    yKeys.forEach((key) => {
      for (let i = 0; i < cloned.length; i++) {
        if (cloned[i][key] == null || isNaN(cloned[i][key])) {
          let prev = i - 1;
          let next = i + 1;
          while (prev >= 0 && (cloned[prev][key] == null || isNaN(cloned[prev][key]))) prev--;
          while (next < cloned.length && (cloned[next][key] == null || isNaN(cloned[next][key]))) next++;
          if (prev >= 0 && next < cloned.length) {
            cloned[i][key] = (cloned[prev][key] + cloned[next][key]) / 2;
          }
        }
      }
    });
    return cloned;
  }

  // ---------- MEMOIZED DATA PREP ----------

  const { interpolatedTempData, tempYKeys } = useMemo(() => {
    const filtered = dispAndLevelData.filter(
      (d) => d.metric_type === "tank_level" && typeof d.avg_temp === "number"
    );
    const { data, yKeys } = prepareTemperatureChartData(filtered);
    return { interpolatedTempData: interpolateMissing(data, yKeys), tempYKeys: yKeys };
  }, [dispAndLevelData]);

  const { interpolatedTankData, tankYKeys } = useMemo(() => {
    const filtered = dispAndLevelData.filter(
      (d) => d.metric_type === "tank_level" && typeof d.value === "number"
    );
    const { data, yKeys } = prepareTankLevelChartData(filtered);
    return { interpolatedTankData: interpolateMissing(data, yKeys), tankYKeys: yKeys };
  }, [dispAndLevelData]);

  const preparedVSLevelData = useMemo(() => prepareChartData(dispVSLevelData), [dispVSLevelData]);

  // ---------- RENDER ----------

  return (
    <Layout>
      <div className="home-container">
        <h2 className="home-header">Dispense VS Tank Level</h2>
        <DescriptionList text="
                        This report shows tank behaviour over time per site.
                        /nYou can filter further on data for the date chosen.
                        /nThe report shows:
                        /nTotal tank volume behaviour vs Total Tank initial volume minus dispensed volume over time. - Please note the drop outs are due to tank/s not sending data witin that interval.
                        /nTank volume behaviour over time per tank. - Please note this is interpolated data.
                        /nPer tank temperature behaviour over time. - Please note this is interpolated data.
                        " 
        />

        {/* Dropdown + Date Picker */}
        <div className="controls">
          <div className="dropdown">
            <DropdownComponent
              options={siteOptions}
              value={code}
              onChange={handleDropdownChange}
              label="Choose code"
            />
            {code && <p className="small">You selected: {code}</p>}
          </div>

          <div className="date-picker-wrapper">
            <button
              className="date-arrow"
              onClick={() => {
                const prev = new Date(selectedFromDate);
                prev.setDate(prev.getDate() - 1);
                handleFromDateChange(prev.toISOString().slice(0, 10));
              }}
            >
              ←
            </button>
            <input
              type="date"
              value={selectedFromDate}
              onChange={(e) => handleFromDateChange(e.target.value)}
            />
            <button
              className="date-arrow"
              onClick={() => {
                const next = new Date(selectedFromDate);
                next.setDate(next.getDate() + 1);
                handleFromDateChange(next.toISOString().slice(0, 10));
              }}
            >→
            </button>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <p>Loading...</p>
        ) : dispAndLevelData.length > 0 ? (
          <div className="data-section">
            <SmallTableComponent data={dispAndLevelData} />

            <div>
              <div>
                <h2 className="normal-heading" style={{ marginTop: "2rem" }}>Gross Tank Volume vs Simulated Depletion</h2> {/* Added spacing below the 1st table and chart */}
                <ReusableLineChart
                  data={preparedVSLevelData}
                  xKey="time_bucket"
                  yKeys={["physical_total_tank_level", "calculated_level_of_tank_after_dispense"]}
                  yKeyAliases={{
                    physical_total_tank_level: "Total tank volume",
                    calculated_level_of_tank_after_dispense: "Calculated tank volume",
                  }}
                  height={400}
                  width={undefined} // Undefined makes the library handle the width
                />
              </div>

              <div>
                <h2 className="normal-heading">Gross Individual Tank Volume over Time</h2>
                <DispLevelAndTempChart
                  data={interpolatedTankData}
                  xKey="timestamp"
                  yKeys={tankYKeys}
                  height={400}
                  width={undefined}
                />
              </div>

              <div>
                <h2 className="normal-heading">Tank Temperature over Time</h2>
                <DispLevelAndTempChart
                  data={interpolatedTempData}
                  xKey="timestamp"
                  yKeys={tempYKeys}
                  height={400}
                  width={undefined}
                />
              </div>
            </div>
          </div>
        ) : (
          <p>No Dispenser preset found for {selectedFromDate}.</p>
        )}
      </div>
    </Layout>
  );
};

export default DispVSLevelComponent;
